import { z } from "zod";

import {
  messagePartUpdatedEventSchema,
  permissionAskedEventSchema,
  permissionRepliedEventSchema,
  questionAskedEventSchema,
} from "./agentTools/opencode";
import { webhookDeliveryStateSchema } from "./cloudflareAgentWebhookResponder/env";
import { connectedChatProviderSchema } from "./publisherInternalAuth";

const connectedChatRoutingMatchSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("messageContainsAny"),
    terms: z
      .array(
        z
          .string()
          .trim()
          .min(1)
          .max(80, "Match terms must be 80 characters or fewer"),
      )
      .min(1, "Enter at least one match term")
      .max(10, "Use up to 10 match terms"),
  }),
]);

export const connectedChatRoutingRuleSchema = z
  .object({
    id: z.uuid(),
    match: connectedChatRoutingMatchSchema,
    projectId: z.uuid(),
  })
  .describe("ConnectedChatRoutingRule");

export const connectedChatRoutingPolicySchema = z
  .object({
    version: z.literal(1),
    defaultProjectId: z.uuid(),
    rules: z.array(connectedChatRoutingRuleSchema),
  })
  .describe("ConnectedChatRoutingPolicy");

export const connectedChatRoutingReasonSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("existingSession"),
    }),
    z.object({
      type: z.literal("rule"),
      ruleId: z.uuid(),
    }),
    z.object({
      type: z.literal("workspaceDefault"),
    }),
  ])
  .describe("ConnectedChatRoutingReason");

export const sessionPromptFileSchema = z
  .object({
    filename: z.string().min(1),
    mime: z.string().min(1),
    url: z.string().min(1),
  })
  .describe("SessionPromptFile");

export const connectedChatAttachmentSchema = z
  .object({
    data: z
      .custom()
      .describe("override?:ArrayBuffer | ArrayBufferView | Blob | null"),
    fetchData: z
      .custom((value) => typeof value === "function")
      .describe(
        "override?:(() => ArrayBuffer | ArrayBufferView | Blob | null | Promise<ArrayBuffer | ArrayBufferView | Blob | null>)",
      ),
    fetchMetadata: z.object({ url: z.string().nullish() }).optional(),
    mimeType: z.string().nullish(),
    name: z.string().nullish(),
    size: z.number().nullish(),
    type: z.string().optional(),
    url: z.string().nullish(),
  })
  .describe("ConnectedChatAttachment");

export const connectedChatPostableMessageSchema = z
  .union([z.string(), z.record(z.string(), z.unknown())])
  .describe("ConnectedChatPostableMessage");

export const connectedChatSentMessageSchema = z
  .object({
    edit: z
      .custom((value) => typeof value === "function")
      .describe(
        "override:(message: ConnectedChatPostableMessage) => Promise<ConnectedChatSentMessage>",
      ),
    id: z.string().min(1),
  })
  .describe("ConnectedChatSentMessage");

export const connectedChatMessageSchema = z
  .object({
    attachments: z.array(connectedChatAttachmentSchema),
    author: z.object({ userId: z.string().min(1) }).optional(),
    id: z.string().min(1),
    text: z.string(),
  })
  .describe("ConnectedChatMessage");

export const connectedChatThreadSchema = z
  .object({
    post: z
      .custom((value) => typeof value === "function")
      .describe(
        "override:(message: ConnectedChatPostableMessage) => Promise<ConnectedChatSentMessage>",
      ),
    subscribe: z
      .custom((value) => typeof value === "function")
      .describe("override:() => Promise<void>"),
  })
  .describe("ConnectedChatThread");

export const connectedChatRuntimeSchema = z
  .object({
    onNewMention: z
      .custom((value) => typeof value === "function")
      .describe(
        "override:(callback: (thread: ConnectedChatThread, message: ConnectedChatMessage) => void | Promise<void>) => void",
      ),
    onSubscribedMessage: z
      .custom((value) => typeof value === "function")
      .describe(
        "override:(callback: (thread: ConnectedChatThread, message: ConnectedChatMessage) => void | Promise<void>) => void",
      ),
  })
  .describe("ConnectedChatRuntime");

// The snapshot `timeline` entries: assistant narration and tool calls in
// chronological order.
export const connectedChatTimelineTextEntrySchema = z
  .object({
    type: z.literal("text"),
    id: z.string().optional(),
    text: z.string(),
  })
  .describe("ConnectedChatTimelineTextEntry");

export const connectedChatTimelineToolEntrySchema = z
  .object({
    type: z.literal("tool"),
    tool: z.unknown(),
  })
  .describe("ConnectedChatTimelineToolEntry");

// The todowrite tool model's accumulated todo list, as renderers read it.
export const connectedChatTodoWriteToolSchema = z
  .object({
    content: z.object({
      kind: z.literal("todoWrite"),
      state: z.object({
        todos: z.array(
          z.object({
            content: z.string(),
            id: z.string(),
            status: z.string(),
          }),
        ),
      }),
    }),
  })
  .describe("ConnectedChatTodoWriteTool");

export const connectedChatSessionSnapshotSchema = z
  .object({
    text: z.string(),
    // The turn was stopped (aborted). Surfaces canceled as a status rather than
    // a raw "This request has been canceled." body line.
    cancelled: z.boolean().optional(),
    // The turn died on a session error — the settled card must show a failure,
    // not a checkmark.
    errored: z.boolean().optional(),
    todos: z.array(
      z.object({
        content: z.string(),
        id: z.string(),
        priority: z.string(),
        status: z.string(),
      }),
    ),
    tools: z.array(z.unknown()),
    // Assistant narration and tool calls in chronological order, so the Slack
    // card can render them interleaved rather than as a flat tool list.
    // Optional: an absent timeline falls back to rendering `tools` alone.
    timeline: z
      .array(
        z.union([
          connectedChatTimelineTextEntrySchema,
          connectedChatTimelineToolEntrySchema,
        ]),
      )
      .optional(),
  })
  .describe("ConnectedChatSessionSnapshot");

export const connectedChatSessionContextSchema = z
  .object({
    message: connectedChatMessageSchema,
    sessionId: z.string().min(1),
    thread: connectedChatThreadSchema,
  })
  .describe("ConnectedChatSessionContext");

const optionalConnectedChatStatusStringSchema = z
  .string()
  .optional()
  .catch(undefined);

export const connectedChatStatusToolSchema = z
  .object({
    debug: z
      .object({ error: optionalConnectedChatStatusStringSchema })
      .optional()
      .catch(undefined),
    display: z
      .object({
        header: optionalConnectedChatStatusStringSchema,
        summary: optionalConnectedChatStatusStringSchema,
      })
      .optional()
      .catch(undefined),
    icon: optionalConnectedChatStatusStringSchema,
    status: optionalConnectedChatStatusStringSchema,
    toolName: z.string(),
  })
  .describe("ConnectedChatStatusTool");

const optionalAgentSessionIdSchema = z.string().min(1).optional();

export const connectedChatAgentEventSchema = z
  .object({
    agentSessionId: optionalAgentSessionIdSchema,
    sessionId: z.string().min(1),
    seq: z.number().int().nonnegative(),
    snapshot: connectedChatSessionSnapshotSchema,
    isFinal: z.boolean(),
  })
  .describe("ConnectedChatAgentEvent");

export const connectedChatOpencodeRequestEventSchema = z
  .discriminatedUnion("type", [
    permissionAskedEventSchema.extend({
      agentSessionId: optionalAgentSessionIdSchema,
      sessionId: z.uuid(),
    }),
    questionAskedEventSchema.extend({
      agentSessionId: optionalAgentSessionIdSchema,
      sessionId: z.uuid(),
    }),
  ])
  .describe("ConnectedChatOpencodeRequestEvent");

export const connectedChatResponderEventSchema = z
  .union([
    connectedChatAgentEventSchema.describe("ignoreImport:true"),
    connectedChatOpencodeRequestEventSchema,
    z.discriminatedUnion("type", [
      messagePartUpdatedEventSchema.extend({
        agentSessionId: optionalAgentSessionIdSchema,
        sessionId: z.uuid(),
      }),
      permissionRepliedEventSchema.extend({
        agentSessionId: optionalAgentSessionIdSchema,
        sessionId: z.uuid(),
      }),
    ]),
  ])
  .describe("ConnectedChatResponderEvent");

export const pendingConnectedChatEventSchema = connectedChatResponderEventSchema
  .and(z.object({ deliveryId: z.uuid() }))
  .describe("PendingConnectedChatEvent");

export const connectedChatAgentEventResponseSchema = z
  .object({ ok: z.boolean() })
  .describe("ConnectedChatAgentEventResponse");

export const connectedChatPreparedSessionSchema = z
  .discriminatedUnion("kind", [
    z.object({ kind: z.literal("skip") }),
    z.object({
      kind: z.literal("ready"),
      // The runtime sends `send` to the coordinator over HTTP and stores
      // `deliveryState` so the out-of-band agent-event callback can render
      // snapshots back to the originating chat platform. Both are
      // platform-agnostic: the provider built the delivery state at prompt time.
      send: z.object({
        projectId: z.string().min(1),
        userId: z.string().min(1),
        sessionId: z.string().min(1),
        parts: z
          .custom((value) => Array.isArray(value))
          .describe("override:UIMessagePart[]"),
        sourceContext: z.custom().describe("override:OpencodeSourceContext"),
        systemContext: z.string().optional(),
      }),
      deliveryState: webhookDeliveryStateSchema,
    }),
  ])
  .describe("ConnectedChatPreparedSession");

export const connectedChatRuntimeOptionsSchema = z
  .object({
    chat: connectedChatRuntimeSchema,
    getSessionId: z
      .custom((value) => typeof value === "function")
      .describe(
        "override?:(input: { message: ConnectedChatMessage; thread: ConnectedChatThread }) => string | Promise<string>",
      ),
    prepareSession: z
      .custom((value) => typeof value === "function")
      .describe(
        "override:(input: ConnectedChatSessionContext) => ConnectedChatPreparedSession | Promise<ConnectedChatPreparedSession>",
      ),
  })
  .describe("ConnectedChatRuntimeOptions");

export const connectedChatAccessRuntimeSchema = z
  .object({
    isFirstConnectedChatUse: z.boolean(),
    isPendingRegistration: z.boolean(),
    project: z.object({ id: z.uuid(), name: z.string().nullable() }),
    routingReason: connectedChatRoutingReasonSchema,
    userId: z.uuid(),
    workspaceId: z.uuid(),
  })
  .describe("ConnectedChatAccessRuntime");

export const connectedChatAccessResultSchema = z
  .discriminatedUnion("status", [
    z.object({
      status: z.literal("ready"),
      runtime: connectedChatAccessRuntimeSchema,
    }),
    z.object({
      status: z.literal("approvalRequired"),
      ownerEmails: z.array(z.email()),
      projectId: z.uuid(),
    }),
    z.object({
      status: z.literal("notConfigured"),
    }),
    z.object({
      status: z.literal("routingError"),
    }),
    z.object({
      status: z.literal("projectAccessDenied"),
      projectName: z.string().nullable(),
    }),
  ])
  .describe("ConnectedChatAccessResult");

export const connectedChatAccessOptionsSchema = z
  .object({
    actorEmail: z.email().nullable(),
    actorId: z.string().min(1),
    channelId: z.string().min(1).nullable(),
    provider: connectedChatProviderSchema,
    nangoConnectionId: z.string().min(1),
    workspaceId: z.string().min(1),
  })
  .describe("ConnectedChatAccessOptions");

const connectedChatAccessRequestBaseSchema = connectedChatAccessOptionsSchema
  .omit({ provider: true })
  .extend({ sessionId: z.string().min(1) });

export const connectedChatAccessRequestOptionsSchema = z
  .discriminatedUnion("type", [
    connectedChatAccessRequestBaseSchema.extend({
      type: z.literal("prompt"),
      messageText: z.string().min(1),
    }),
    connectedChatAccessRequestBaseSchema.extend({
      type: z.literal("sessionAction"),
    }),
  ])
  .describe("ConnectedChatAccessRequestOptions");

export const connectedChatInviteApprovalOptionsSchema = z
  .object({
    actorEmail: z.email(),
    actorId: z.string().min(1),
    channelId: z.string().min(1).nullable(),
    provider: connectedChatProviderSchema,
    inviteeEmail: z.email(),
    nangoConnectionId: z.string().min(1),
    workspaceId: z.string().min(1),
  })
  .describe("ConnectedChatInviteApprovalOptions");

export const connectedChatConnectionLookupOptionsSchema = z
  .object({
    provider: connectedChatProviderSchema,
    nangoConnectionId: z.string().min(1),
    workspaceId: z.string().min(1),
  })
  .describe("ConnectedChatConnectionLookupOptions");
